import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearAgencySessionCache } from "@/lib/agency-cache";

const AUTH_STORAGE_KEY = `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`;
const SESSION_VALIDATION_RETRY_DELAYS = [0, 400, 1200];

export const clearPersistedAuthSession = async () => {
  clearAgencySessionCache();

  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // Ignore invalid-session signout failures and clear storage below.
  }

  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

export const loadValidatedSession = async (): Promise<{
  session: Session | null;
  user: User | null;
  invalid: boolean;
}> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    return { session: null, user: null, invalid: false };
  }

  let lastError: string | undefined;

  for (const delay of SESSION_VALIDATION_RETRY_DELAYS) {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (user && !error) {
      return { session, user, invalid: false };
    }

    lastError = error?.message;
  }

  console.warn("Session validation failed during boot, keeping cached session", lastError);
  return { session, user: session.user, invalid: false };
};
